/* =============================================================================
 * coachAPI — the college-coach portal client (KGH-795)
 * -----------------------------------------------------------------------------
 * Modelled on services/api.ts `apiCall`, deliberately NOT on staffAPI's
 * `staffFetch`. The coach UI branches on `error.code` in a dozen places —
 * pending, rejected, suspended, three quotas, list full, duplicate name — and
 * staffFetch throws a bare Error that discards the code. Everything thrown from
 * here is a CoachApiError carrying `status`, `code`, `details` and, on a 429,
 * `retryAfterSeconds`.
 *
 * Five things this file exists to get right, each of which is a bug if the
 * caller has to remember it:
 *  1. The base URL comes from BACKEND_CONFIG only. It already ends in /api; the
 *     edge strips it, which is why the server mounts /coach and we call
 *     /api/coach/...
 *  2. Repeatable query params are sent as REPEATED KEYS, and travel_club is
 *     matched on the option's `key`, never its display `value`.
 *  3. A cursor is bound to its sort. Callers reset it on every filter, sort or
 *     preference change; this module never replays one on its own.
 *  4. export.csv is not JSON and cannot be an <a download> — it is a GET that
 *     requires the Authorization header, so an anchor sends no token and saves
 *     the 401 envelope as a file. downloadListExport() fetches it properly.
 *  5. Opening a profile is a WRITE: the server logs the view and notifies the
 *     athlete's family, once per staff per athlete per day. Never prefetch it,
 *     never call it to warm a cache.
 * ========================================================================== */
import { BACKEND_CONFIG } from '../config/backendConfig';
import { handleUnauthorized } from './api';
import { swrGet, swrSet, swrInvalidate } from '../utils/swrCache';
import type {
  CoachApiError,
  CoachInviteLookup,
  CoachMeResponse,
  CoachOpenInvite,
  CoachRegisterRequest,
  CoachRegisterResponse,
  CoachActivityProfileResponse,
  CoachPreferencesResponse,
  CoachPreferencesUpdate,
  CoachProfileUpdate,
  CoachPlayersQuery,
  CoachPlayersResponse,
  CoachFilterOptionsResponse,
  CoachPlayerDetailResponse,
  CoachEvent,
  CoachEventsResponse,
  CoachListsResponse,
  CoachListResponse,
  CoachListDetailResponse,
  CoachListMembershipsResponse,
  CoachPassedMode,
  CoachPassesResponse,
  CoachPassResponse,
  CoachUnpassResponse,
  CoachCreateListRequest,
  CoachUpdateListRequest,
  CoachTagRequest,
  CoachTagResponse,
  CoachUntagResponse,
  CoachDeleteListResponse,
  CoachExportPreviewResponse,
  CoachExportFile,
  CoachTournamentsQuery,
  CoachTournamentsResponse,
  CoachTournamentDetailResponse,
  CoachAttendanceResponse,
} from './coachAPI.types';

/* ── token ──────────────────────────────────────────────────────────────── */

/**
 * The APS session token, from the key AuthContext writes. Inside a component
 * prefer `const { sessionToken } = useAuth()` and pass it in — this fallback is
 * for the call sites that are not inside the provider tree.
 */
export function getCoachToken(): string | null {
  try {
    return sessionStorage.getItem('sessionToken') || localStorage.getItem('sessionToken');
  } catch {
    return null;
  }
}

/* ── errors ─────────────────────────────────────────────────────────────── */

function toCoachError(status: number, body: any, retryAfterHeader: string | null): CoachApiError {
  const envelope = body?.error && typeof body.error === 'object' ? body.error : null;
  const message =
    envelope?.message ||
    (typeof body?.error === 'string' ? body.error : null) ||
    body?.message ||
    `HTTP ${status}`;
  const err = new Error(message) as CoachApiError;
  err.status = status;
  err.code = envelope?.code || body?.code || `HTTP_${status}`;
  err.details = envelope || body || { code: err.code, message };
  err.body = body;
  const retry =
    (envelope?.retry_after_seconds as number | undefined) ??
    (retryAfterHeader ? Number(retryAfterHeader) : undefined);
  if (retry !== undefined && Number.isFinite(retry)) err.retryAfterSeconds = retry;
  return err;
}

/** Narrow an unknown catch value to the coach error shape. */
export function isCoachApiError(e: unknown): e is CoachApiError {
  return !!e && typeof e === 'object' && 'code' in (e as object) && 'status' in (e as object);
}

function toOpenInvite(value: unknown): CoachOpenInvite | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as { school_id?: unknown; school_name?: unknown };
  const schoolId = typeof v.school_id === 'number' ? v.school_id : Number(v.school_id);
  if (!Number.isInteger(schoolId) || schoolId <= 0) return null;
  return {
    school_id: schoolId,
    school_name: typeof v.school_name === 'string' && v.school_name.trim() ? v.school_name.trim() : null,
  };
}

/**
 * The open invite GET /coach/me reports for an account with no coach profile
 * yet. Read from a 200 body or from a refusal (inside the error envelope or
 * beside it), so the sign-up screen does not depend on which one the server
 * answers a not-yet-coach with.
 */
export function readOpenInvite(source: unknown): CoachOpenInvite | null {
  if (!source || typeof source !== 'object') return null;
  const s = source as { open_invite?: unknown; details?: { open_invite?: unknown }; body?: { open_invite?: unknown } };
  return (
    toOpenInvite(s.open_invite) ||
    toOpenInvite(s.details?.open_invite) ||
    toOpenInvite(s.body?.open_invite)
  );
}

/**
 * A GET /coach/me answer for an account that has not finished coach sign-up:
 * no coach profile yet, or a profile that never got its program. Such an
 * account belongs on /coach/signup, not on the waiting screen.
 */
export function isCoachSetupUnfinished(res: Partial<CoachMeResponse> | null | undefined): boolean {
  if (!res || !res.coach || !res.access) return true;
  return res.access.status === 'incomplete';
}

/**
 * GET /coach/invites/lookup?token=... Public, no session. Resolves the invite
 * behind an invite link, or throws a CoachApiError (404 INVITE_NOT_FOUND for a
 * link that is no longer good, carrying the server's own message).
 */
export async function lookupCoachInvite(token: string, signal?: AbortSignal): Promise<CoachInviteLookup> {
  const res = await coachCall<{ invite?: Partial<CoachInviteLookup> }>(
    `/coach/invites/lookup?token=${encodeURIComponent(token)}`,
    {},
    // No bearer: the person clicking the link has no account yet, and a stale
    // token from another tab must not ride along.
    { token: '', signal }
  );
  const invite = res?.invite;
  const program = toOpenInvite(invite);
  const email = typeof invite?.email === 'string' ? invite.email.trim() : '';
  if (!program || !email) {
    const err = new Error('This invite link is no longer valid. Sign up below.') as CoachApiError;
    err.status = 404;
    err.code = 'INVITE_NOT_FOUND';
    err.details = { code: err.code, message: err.message };
    throw err;
  }
  return { email, ...program };
}

/** True for the three refusals that mean "your account cannot see athletes". */
export function isAccessError(e: unknown): boolean {
  return (
    isCoachApiError(e) &&
    ['COACH_ACCOUNT_REQUIRED', 'COACH_REGISTRATION_INCOMPLETE', 'COACH_VERIFICATION_PENDING',
     'COACH_VERIFICATION_REJECTED', 'COACH_ACCOUNT_SUSPENDED'].includes(String(e.code))
  );
}

/* ── the fetch ──────────────────────────────────────────────────────────── */

export interface CoachRequestOptions {
  /** Pass `useAuth().sessionToken` from a component. */
  token?: string | null;
  signal?: AbortSignal;
}

/**
 * Exported so the internal verification desk (services/coachAdminAPI) sends its
 * requests through exactly this envelope handling rather than growing a second
 * copy of it. The admin desk is not a coach, but the transport is identical:
 * bearer token, JSON, and an `{ error: { code, message } }` body that callers
 * branch on by code.
 */
export async function coachCall<T>(
  endpoint: string,
  init: RequestInit = {},
  opts: CoachRequestOptions = {}
): Promise<T> {
  const token = opts.token ?? getCoachToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string>),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${BACKEND_CONFIG.baseURL}${endpoint}`, {
      ...init,
      headers,
      signal: opts.signal,
    });
  } catch (e) {
    // An aborted request is the caller's own doing — a filter changed, the page
    // unmounted — and every caller already ignores it. Rethrow it untouched.
    if ((e as Error)?.name === 'AbortError') throw e;
    /*
     * Everything else here is the transport failing before any response
     * existed: offline, DNS, a dropped connection. The browser's own words for
     * that are "Failed to fetch", and the error card rendered them at a coach,
     * verbatim, inside an otherwise carefully written product.
     */
    const err = new Error(
      'We could not reach APS Recruits. Check your connection and try again.'
    ) as CoachApiError;
    err.status = 0;
    err.code = 'NETWORK_UNAVAILABLE';
    err.details = { code: 'NETWORK_UNAVAILABLE', message: err.message };
    throw err;
  }

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(endpoint);
    let body: any = {};
    try {
      body = await response.json();
    } catch {
      /* a non-JSON failure body is still a failure */
    }
    throw toCoachError(response.status, body, response.headers.get('retry-after'));
  }

  return (await response.json()) as T;
}

/* ── query building ─────────────────────────────────────────────────────── */

/**
 * Repeatable params go out as repeated keys (`state=GA&state=TX`), which is
 * what the server parses. Empty arrays, empty strings, null and undefined are
 * omitted entirely rather than sent blank — a blank value is a 400, not a
 * no-op.
 */
export function buildCoachQuery(query: CoachPlayersQuery | Record<string, unknown>): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== undefined && v !== null && v !== '') params.append(key, String(v));
      });
      return;
    }
    params.append(key, String(value));
  });
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

/* ── caching ────────────────────────────────────────────────────────────── */

const CACHE = {
  me: 'coach:me',
  preferences: 'coach:preferences',
  filterOptions: 'coach:filterOptions',
  activityProfile: 'coach:activityProfile',
  lists: 'coach:lists',
  memberships: 'coach:memberships',
  filterOptionsFor: (mode: CoachPassedMode) => `coach:filterOptions:${mode}`,
  passes: 'coach:passes',
  firstPage: (qs: string) => `coach:players:${qs}`,
  tournaments: (qs: string) => `coach:tournaments:${qs}`,
};

const MINUTE = 60 * 1000;

/** Drop every cached coach payload. Called after anything that changes what the grid should say. */
export function invalidateCoachCache(): void {
  swrInvalidate((key) => key.startsWith('coach:'));
}

/**
 * After a pass or an un-pass. The grid's first pages and the sidebar counts
 * both hide passed players, so they go too.
 */
export function invalidateCoachPassCache(): void {
  swrInvalidate(
    (key) => key === CACHE.passes || key.startsWith('coach:players:') || key.startsWith('coach:filterOptions')
  );
}

/** Drop only the tag-state caches (after a tag, untag or list edit). */
export function invalidateCoachListCache(): void {
  swrInvalidate((key) => key === CACHE.lists || key === CACHE.memberships);
}

/**
 * Every cached tournament page. Marking or un-marking an event changes the
 * `attending` block on a row that may be sitting in any of them, so they all go
 * rather than being patched — a browse that disagrees with the toast is worse
 * than one extra request.
 */
export function invalidateCoachTournamentCache(): void {
  swrInvalidate((key) => key.startsWith('coach:tournaments:'));
}

/* ── the surface ────────────────────────────────────────────────────────── */

export const coachAPI = {
  /* account ------------------------------------------------------------- */

  /**
   * Who am I, what program am I attached to, and may I see athletes. Cached
   * briefly so a route change does not re-ask; `fresh` forces the network.
   */
  async me(opts: CoachRequestOptions & { fresh?: boolean } = {}): Promise<CoachMeResponse> {
    if (!opts.fresh) {
      const cached = swrGet<CoachMeResponse>(CACHE.me, 2 * MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachMeResponse>('/coach/me', {}, opts);
    swrSet(CACHE.me, res);
    return res;
  },

  /**
   * "Check again" on the waiting screen. Re-runs the invite and coach-directory
   * checks for a pending coach, so one added to the directory after sign-up
   * gets in without waiting on review. Same body as me().
   *
   * A 404 means the server predates the route; fall back to a fresh me() so
   * the button still reports the current status instead of an error page.
   */
  async recheck(opts: CoachRequestOptions = {}): Promise<CoachMeResponse> {
    let res: CoachMeResponse;
    try {
      res = await coachCall<CoachMeResponse>('/coach/recheck', { method: 'POST' }, opts);
    } catch (e) {
      if (isCoachApiError(e) && e.status === 404) return coachAPI.me({ ...opts, fresh: true });
      throw e;
    }
    invalidateCoachCache();
    swrSet(CACHE.me, res);
    return res;
  },

  register(body: CoachRegisterRequest, opts: CoachRequestOptions = {}): Promise<CoachRegisterResponse> {
    invalidateCoachCache();
    return coachCall<CoachRegisterResponse>('/coach/register', { method: 'POST', body: JSON.stringify(body) }, opts);
  },

  /**
   * Edit my own display name and title, and whether families see them.
   *
   * This is the name families read in "<name> (<title>) from <school> viewed
   * your profile", so it is not decoration. With hide_name_on_views on, the
   * notice reads "A coach from <school> viewed your profile." Send only what
   * changed; an omitted key keeps its saved value. The response is the same
   * shape as me(), so it replaces the cache.
   */
  async updateMe(body: CoachProfileUpdate, opts: CoachRequestOptions = {}): Promise<CoachMeResponse> {
    const res = await coachCall<CoachMeResponse>(
      '/coach/me',
      { method: 'PATCH', body: JSON.stringify(body) },
      opts
    );
    // The name rides along on every profile-view notification, so anything
    // holding a cached copy of it is now stale.
    invalidateCoachCache();
    swrSet(CACHE.me, res);
    return res;
  },

  /* preferences --------------------------------------------------------- */

  async getPreferences(opts: CoachRequestOptions & { fresh?: boolean } = {}): Promise<CoachPreferencesResponse> {
    if (!opts.fresh) {
      const cached = swrGet<CoachPreferencesResponse>(CACHE.preferences, 2 * MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachPreferencesResponse>('/coach/preferences', {}, opts);
    swrSet(CACHE.preferences, res);
    return res;
  },

  /**
   * PATCH semantics: an absent key keeps its saved value, null or [] clears it,
   * and `metric_thresholds: {}` means "use my program's numbers". Saving
   * changes every rating, so the whole coach cache goes with it.
   */
  async updatePreferences(body: CoachPreferencesUpdate, opts: CoachRequestOptions = {}): Promise<CoachPreferencesResponse> {
    const res = await coachCall<CoachPreferencesResponse>(
      '/coach/preferences',
      { method: 'PUT', body: JSON.stringify(body) },
      opts
    );
    invalidateCoachCache();
    swrSet(CACHE.preferences, res);
    return res;
  },

  /**
   * "Your recruiting activity" — the coach's own behaviour profile
   * (KGH-795 / APS-255).
   *
   * Read-only and side-effect free: opening your own profile is not behaviour,
   * so unlike getPlayer() this is safe to prefetch and safe to cache. Cached
   * for a minute — the numbers move as the coach uses the grid in another tab,
   * and a panel that claims to show what they just did should not be five
   * minutes behind.
   */
  async getActivityProfile(opts: CoachRequestOptions & { fresh?: boolean } = {}): Promise<CoachActivityProfileResponse> {
    if (!opts.fresh) {
      const cached = swrGet<CoachActivityProfileResponse>(CACHE.activityProfile, MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachActivityProfileResponse>('/coach/activity-profile', {}, opts);
    swrSet(CACHE.activityProfile, res);
    return res;
  },

  /* the grid ------------------------------------------------------------ */

  /**
   * One page of athletes. Only the FIRST page of a given query is cached — a
   * cursor page is bound to its sort and to the moment it was issued, and
   * painting a stale one produces a grid that disagrees with itself.
   */
  async getPlayers(query: CoachPlayersQuery = {}, opts: CoachRequestOptions = {}): Promise<CoachPlayersResponse> {
    const qs = buildCoachQuery(query as Record<string, unknown>);
    const isFirstPage = !query.cursor;
    if (isFirstPage) {
      const cached = swrGet<CoachPlayersResponse>(CACHE.firstPage(qs), MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachPlayersResponse>(`/coach/players${qs}`, {}, opts);
    if (isFirstPage) swrSet(CACHE.firstPage(qs), res);
    return res;
  },

  /**
   * The sidebar's options and counts. The counts leave out this coach's passed
   * players unless `passed` says otherwise, so they match the grid.
   */
  async getFilterOptions(
    opts: CoachRequestOptions & { fresh?: boolean; passed?: CoachPassedMode } = {}
  ): Promise<CoachFilterOptionsResponse> {
    const mode = opts.passed ?? 'exclude';
    const key = CACHE.filterOptionsFor(mode);
    if (!opts.fresh) {
      const cached = swrGet<CoachFilterOptionsResponse>(key, 15 * MINUTE);
      if (cached) return cached;
    }
    // Always sent. With no ?passed the server shows everyone.
    const qs = `?passed=${mode}`;
    const res = await coachCall<CoachFilterOptionsResponse>(`/coach/players/filter-options${qs}`, {}, opts);
    swrSet(key, res);
    return res;
  },

  /**
   * The full profile. NOT CACHED AND NEVER PREFETCHED: the server treats this
   * call as the coach viewing the athlete, writes a ledger row, and notifies
   * the athlete and their guardians. Call it when a coach asked for it.
   */
  getPlayer(
    athleteId: string,
    opts: CoachRequestOptions & { source?: string } = {}
  ): Promise<CoachPlayerDetailResponse> {
    const qs = opts.source ? `?source=${encodeURIComponent(opts.source)}` : '';
    return coachCall<CoachPlayerDetailResponse>(`/coach/players/${encodeURIComponent(athleteId)}${qs}`, {}, opts);
  },

  /* passes -------------------------------------------------------------- */

  /** Ids of the players this coach passed on, newest first. Families are never told. */
  async getPasses(opts: CoachRequestOptions & { fresh?: boolean } = {}): Promise<CoachPassesResponse> {
    if (!opts.fresh) {
      const cached = swrGet<CoachPassesResponse>(CACHE.passes, MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachPassesResponse>('/coach/passes', {}, opts);
    swrSet(CACHE.passes, res);
    return res;
  },

  async passAthlete(athleteId: string, opts: CoachRequestOptions = {}): Promise<CoachPassResponse> {
    const res = await coachCall<CoachPassResponse>(
      '/coach/passes',
      { method: 'POST', body: JSON.stringify({ athlete_id: athleteId }) },
      opts
    );
    invalidateCoachPassCache();
    return res;
  },

  async unpassAthlete(athleteId: string, opts: CoachRequestOptions = {}): Promise<CoachUnpassResponse> {
    const res = await coachCall<CoachUnpassResponse>(
      `/coach/passes/${encodeURIComponent(athleteId)}`,
      { method: 'DELETE' },
      opts
    );
    invalidateCoachPassCache();
    return res;
  },

  /* activity ------------------------------------------------------------ */

  /**
   * Client-side activity, one event or a batch of up to 50. 'profile_view' is
   * refused by the server on purpose — it writes that itself.
   * Never let an analytics failure reach the UI: it is not the coach's problem.
   */
  async recordEvents(events: CoachEvent | CoachEvent[], opts: CoachRequestOptions = {}): Promise<CoachEventsResponse | null> {
    const body = Array.isArray(events) ? { events } : events;
    try {
      return await coachCall<CoachEventsResponse>('/coach/events', { method: 'POST', body: JSON.stringify(body) }, opts);
    } catch {
      return null;
    }
  },

  /* lists --------------------------------------------------------------- */

  async getLists(opts: CoachRequestOptions & { fresh?: boolean } = {}): Promise<CoachListsResponse> {
    if (!opts.fresh) {
      const cached = swrGet<CoachListsResponse>(CACHE.lists, MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachListsResponse>('/coach/lists', {}, opts);
    swrSet(CACHE.lists, res);
    return res;
  },

  /** athlete id -> the ids of every list it sits in. One call tags a whole page. */
  async getMemberships(opts: CoachRequestOptions & { fresh?: boolean } = {}): Promise<CoachListMembershipsResponse> {
    if (!opts.fresh) {
      const cached = swrGet<CoachListMembershipsResponse>(CACHE.memberships, MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachListMembershipsResponse>('/coach/lists/memberships', {}, opts);
    swrSet(CACHE.memberships, res);
    return res;
  },

  /** Ids only, never cards — routes/coach.js owns the card contract. */
  getListDetail(listId: string, opts: CoachRequestOptions = {}): Promise<CoachListDetailResponse> {
    return coachCall<CoachListDetailResponse>(`/coach/lists/${encodeURIComponent(listId)}`, {}, opts);
  },

  /** `color` must be one of `limits.colors` from getLists, or the server 400s. */
  async createList(body: CoachCreateListRequest, opts: CoachRequestOptions = {}): Promise<CoachListResponse> {
    const res = await coachCall<CoachListResponse>('/coach/lists', { method: 'POST', body: JSON.stringify(body) }, opts);
    invalidateCoachListCache();
    return res;
  },

  async updateList(listId: string, body: CoachUpdateListRequest, opts: CoachRequestOptions = {}): Promise<CoachListResponse> {
    const res = await coachCall<CoachListResponse>(
      `/coach/lists/${encodeURIComponent(listId)}`,
      { method: 'PUT', body: JSON.stringify(body) },
      opts
    );
    invalidateCoachListCache();
    return res;
  },

  async deleteList(listId: string, opts: CoachRequestOptions = {}): Promise<CoachDeleteListResponse> {
    const res = await coachCall<CoachDeleteListResponse>(
      `/coach/lists/${encodeURIComponent(listId)}`,
      { method: 'DELETE' },
      opts
    );
    invalidateCoachListCache();
    return res;
  },

  /**
   * Tag one athlete or a batch (max 200). `'default'` is a valid list id and
   * resolves to the pinned list, creating it if this coach has never had one —
   * which is what a one-tap heart on a grid card should use, because it works
   * before the portal has fetched a single list.
   *
   * The response reports honestly: `added`, `already_tagged`, `unavailable`,
   * `not_added_list_full`. A batch of 40 that added 3 must say 3.
   */
  async tagAthletes(listId: string, body: CoachTagRequest, opts: CoachRequestOptions = {}): Promise<CoachTagResponse> {
    const res = await coachCall<CoachTagResponse>(
      `/coach/lists/${encodeURIComponent(listId)}/athletes`,
      { method: 'POST', body: JSON.stringify(body) },
      opts
    );
    invalidateCoachListCache();
    return res;
  },

  async untagAthlete(listId: string, athleteId: string, opts: CoachRequestOptions = {}): Promise<CoachUntagResponse> {
    const res = await coachCall<CoachUntagResponse>(
      `/coach/lists/${encodeURIComponent(listId)}/athletes/${encodeURIComponent(athleteId)}`,
      { method: 'DELETE' },
      opts
    );
    invalidateCoachListCache();
    return res;
  },

  /** Bulk untag. The ids travel in the BODY of the DELETE. */
  async untagAthletes(listId: string, athleteIds: string[], opts: CoachRequestOptions = {}): Promise<CoachUntagResponse> {
    const res = await coachCall<CoachUntagResponse>(
      `/coach/lists/${encodeURIComponent(listId)}/athletes`,
      { method: 'DELETE', body: JSON.stringify({ athlete_ids: athleteIds }) },
      opts
    );
    invalidateCoachListCache();
    return res;
  },

  /* export -------------------------------------------------------------- */

  /**
   * Costs nothing against the daily allowance, and answers `can_export` plus
   * the whole quota — so the button can say "4 of 20 exports left today"
   * instead of letting the coach find the limit by hitting it.
   */
  getExportPreview(listId: string, opts: CoachRequestOptions = {}): Promise<CoachExportPreviewResponse> {
    return coachCall<CoachExportPreviewResponse>(
      `/coach/lists/${encodeURIComponent(listId)}/export-preview`,
      {},
      opts
    );
  },

  /**
   * Fetch the CSV with the session token attached, read the filename and the
   * remaining quota off the response headers, and hand back the text. The
   * caller turns it into a download with `saveCoachExport`.
   *
   * response.ok is checked BEFORE the body is read: a refusal is the usual JSON
   * envelope, and reading it as text would save the error message as a .csv.
   */
  downloadListExport(listId: string, opts: CoachRequestOptions = {}): Promise<CoachExportFile> {
    return fetchCoachExport(
      `/coach/lists/${encodeURIComponent(listId)}/export.csv`,
      '/coach/lists/export.csv',
      'aps-recruits-export.csv',
      opts
    );
  },

  /**
   * The grid's current search as a CSV (Gaines #12). Same file, quota and
   * headers as the list export. Send the grid's filters and sort; the server
   * ignores limit and cursor, so they are not sent. A search over 1,000
   * players is refused with EXPORT_TOO_LARGE, never cut short.
   */
  downloadPlayersExport(query: CoachPlayersQuery, opts: CoachRequestOptions = {}): Promise<CoachExportFile> {
    const { limit, cursor, ...filters } = query;
    return fetchCoachExport(
      `/coach/players/export.csv${buildCoachQuery(filters)}`,
      '/coach/players/export.csv',
      `aps-recruits-players-${localIsoDay()}.csv`,
      opts
    );
  },

  /* tournaments (KGH-795 / APS-255) -------------------------------------- */

  /**
   * The crowd-sourced tournament list: every event athletes have put on their
   * own schedules, with how many this coach may see going and whether this
   * coach has marked it.
   *
   * Only the FIRST page of a given query is cached, for the same reason as the
   * grid: an offset page belongs to the moment it was issued.
   */
  async getTournaments(query: CoachTournamentsQuery = {}, opts: CoachRequestOptions = {}): Promise<CoachTournamentsResponse> {
    const qs = buildCoachQuery(query as Record<string, unknown>);
    const isFirstPage = !query.offset;
    if (isFirstPage) {
      const cached = swrGet<CoachTournamentsResponse>(CACHE.tournaments(qs), MINUTE);
      if (cached) return cached;
    }
    const res = await coachCall<CoachTournamentsResponse>(`/coach/tournaments${qs}`, {}, opts);
    if (isFirstPage) swrSet(CACHE.tournaments(qs), res);
    return res;
  },

  /**
   * One event and the athletes going to it, as the same cards the grid paints.
   *
   * NOT cached: attendance and prospect counts change under the coach's own
   * hands on this screen, and a stale page would argue with the toast they just
   * read. It is a read, not a profile view — nothing here notifies a family.
   */
  getTournament(
    eventId: string,
    query: { limit?: number; offset?: number } = {},
    opts: CoachRequestOptions = {}
  ): Promise<CoachTournamentDetailResponse> {
    const qs = buildCoachQuery(query as Record<string, unknown>);
    return coachCall<CoachTournamentDetailResponse>(
      `/coach/tournaments/${encodeURIComponent(eventId)}${qs}`, {}, opts
    );
  },

  /**
   * "I am attending." Creates — or revives — the tournament list bound to this
   * event; the returned `list.id` is what prospects are tagged into through
   * `tagAthletes`. Families going to the event are told, unless the mark is
   * made private.
   */
  async markAttendingTournament(
    eventId: string,
    body: { visible_to_athletes?: boolean } = {},
    opts: CoachRequestOptions = {}
  ): Promise<CoachAttendanceResponse> {
    const res = await coachCall<CoachAttendanceResponse>(
      `/coach/tournaments/${encodeURIComponent(eventId)}/attendance`,
      { method: 'POST', body: JSON.stringify(body) },
      opts
    );
    invalidateCoachTournamentCache();
    invalidateCoachListCache();
    return res;
  },

  /** Publish or hide an existing mark. Publishing tells families not yet told. */
  async setTournamentVisibility(
    eventId: string,
    visibleToAthletes: boolean,
    opts: CoachRequestOptions = {}
  ): Promise<CoachAttendanceResponse> {
    const res = await coachCall<CoachAttendanceResponse>(
      `/coach/tournaments/${encodeURIComponent(eventId)}/attendance`,
      { method: 'PATCH', body: JSON.stringify({ visible_to_athletes: visibleToAthletes }) },
      opts
    );
    invalidateCoachTournamentCache();
    invalidateCoachListCache();
    return res;
  },

  /**
   * "Not going after all." The tagged prospects are kept, not deleted, so
   * marking the event again brings the same shortlist back.
   */
  async unmarkAttendingTournament(eventId: string, opts: CoachRequestOptions = {}): Promise<CoachAttendanceResponse> {
    const res = await coachCall<CoachAttendanceResponse>(
      `/coach/tournaments/${encodeURIComponent(eventId)}/attendance`,
      { method: 'DELETE' },
      opts
    );
    invalidateCoachTournamentCache();
    invalidateCoachListCache();
    return res;
  },
};

/* ── download helpers ───────────────────────────────────────────────────── */

/**
 * The fetch behind both CSV downloads (a list, and the grid's search). See
 * downloadListExport for what it does and why response.ok is checked first.
 */
async function fetchCoachExport(
  endpoint: string,
  unauthorizedPath: string,
  fallbackFilename: string,
  opts: CoachRequestOptions
): Promise<CoachExportFile> {
  const token = opts.token ?? getCoachToken();
  let response: Response;
  try {
    response = await fetch(`${BACKEND_CONFIG.baseURL}${endpoint}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal: opts.signal,
    });
  } catch (e) {
    if ((e as Error)?.name === 'AbortError') throw e;
    // Same words coachCall uses when the network fails.
    const err = new Error(
      'We could not reach APS Recruits. Check your connection and try again.'
    ) as CoachApiError;
    err.status = 0;
    err.code = 'NETWORK_UNAVAILABLE';
    err.details = { code: 'NETWORK_UNAVAILABLE', message: err.message };
    throw err;
  }

  if (!response.ok) {
    if (response.status === 401) handleUnauthorized(unauthorizedPath);
    let body: any = {};
    try {
      body = await response.json();
    } catch {
      /* ignore */
    }
    throw toCoachError(response.status, body, response.headers.get('retry-after'));
  }

  const csv = await response.text();
  return {
    csv,
    filename: filenameFromDisposition(response.headers.get('content-disposition')) || fallbackFilename,
    rowCount: Number(response.headers.get('x-export-row-count') || 0),
    rowsRemaining: Number(response.headers.get('x-export-rows-remaining') || 0),
    exportsRemaining: Number(response.headers.get('x-export-exports-remaining') || 0),
  };
}

/** Today as YYYY-MM-DD in the coach's own time zone. */
function localIsoDay(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/**
 * Save a fetched export to the coach's machine. Response.text() strips the
 * server's leading BOM, and Excel needs it to read accented names as UTF-8,
 * so it is added back here when missing.
 */
export function saveCoachExport(file: CoachExportFile): void {
  const csv = file.csv.charCodeAt(0) === 0xFEFF ? file.csv : `\uFEFF${file.csv}`;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  // Revoke on the next tick: Safari has not finished reading the blob yet when
  // click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default coachAPI;
